import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal } from './ui/Modal';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { Globe2, Loader, ExternalLink, CheckCircle2, AlertCircle } from 'lucide-react';
import publicApiService from '../services/publicApiService';
import { openExternal } from '../utils/openExternal';

const CATEGORIES = [
  'ai',
  'data',
  'finance',
  'communication',
  'developer-tools',
  'ecommerce',
  'productivity',
  'weather',
  'maps',
  'social',
  'media',
  'gaming',
  'security',
  'other',
];

function categoryLabel(cat) {
  return cat
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function previewSlug(name) {
  const base = (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return base || 'api';
}

export default function PublishToNetworkModal({
  isOpen,
  onClose,
  workspace,
  showToast,
  onPublished,
  onUpgradeRequired,
}) {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusError, setStatusError] = useState('');

  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState('developer-tools');
  const [tagsInput, setTagsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  const wasOpenRef = useRef(false);

  const publishedSlug = status?.slug || status?.public_api?.slug || null;
  const isPublished = status?.status === 'published';
  const slugPreview = publishedSlug || previewSlug(name);
  const listingUrl = status?.url || `https://apiboo.com/apis/${slugPreview}`;

  const tags = useMemo(() => {
    return tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 10);
  }, [tagsInput]);

  useEffect(() => {
    if (!isOpen || !workspace?.id) {
      wasOpenRef.current = false;
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    if (justOpened) {
      setName(workspace.name || '');
      setSummary((workspace.description || '').slice(0, 280));
      setCategory('developer-tools');
      setTagsInput('');
      setStatus(null);
      setStatusError('');
    }

    setLoadingStatus(true);
    publicApiService
      .getNetworkStatus(workspace.id)
      .then((data) => {
        setStatus(data);
        if (data?.public_api) {
          const api = data.public_api;
          setName(api.name || workspace.name || '');
          setSummary((api.summary || workspace.description || '').slice(0, 280));
          setCategory(api.category || 'developer-tools');
          setTagsInput((api.tags || []).join(', '));
        }
      })
      .catch((err) => setStatusError(err.message))
      .finally(() => setLoadingStatus(false));
    // Only re-fetch when the modal opens or the workspace changes. name/
    // description are read at open time for the form defaults; depending on
    // them would re-fetch (and clobber in-progress edits) on a remote rename.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workspace?.id]);

  const handleClose = () => {
    setStatus(null);
    setStatusError('');
    onClose();
  };

  const handlePublish = async (e) => {
    e?.preventDefault();
    if (!workspace?.id || submitting) return;

    if (!name.trim()) {
      showToast?.('Name is required', 'error');
      return;
    }
    if (!summary.trim()) {
      showToast?.('Summary is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const result = await publicApiService.publishToNetwork(workspace.id, {
        name: name.trim(),
        summary: summary.trim(),
        category,
        tags,
      });
      setStatus(result);
      showToast?.(
        isPublished ? 'Listing updated on API Network' : 'Published to API Network',
        'success'
      );
      onPublished?.(result);
    } catch (err) {
      if (err.upgradeRequired || err.status === 402) {
        onUpgradeRequired?.(err.message);
      } else {
        showToast?.(err.message, 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnpublish = async () => {
    if (!workspace?.id || unpublishing) return;
    setUnpublishing(true);
    try {
      await publicApiService.unpublishFromNetwork(workspace.id);
      setStatus({ status: 'draft', slug: publishedSlug });
      showToast?.('Removed from API Network directory', 'success');
      onPublished?.({ status: 'draft' });
    } catch (err) {
      showToast?.(err.message, 'error');
    } finally {
      setUnpublishing(false);
    }
  };

  if (!isOpen || !workspace) return null;

  const categoryOptions = CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Publish to API Network"
      subtitle="List this team workspace on apiboo.com/apis"
      customIcon={<Globe2 size={22} className="text-violet-500" />}
      preventBackdropClose={submitting || unpublishing}
    >
      <form onSubmit={handlePublish} className="space-y-4">
        {loadingStatus && (
          <div className="flex items-center justify-center py-6">
            <Loader size={20} className="animate-spin text-violet-500" />
          </div>
        )}

        {statusError && !loadingStatus && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 p-3">
            <AlertCircle size={15} className="mt-0.5 flex-shrink-0 text-red-500" />
            <p className="text-sm text-red-600 dark:text-red-400">{statusError}</p>
          </div>
        )}

        {!loadingStatus && (
          <>
            {isPublished && (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 size={16} />
                <span className="font-medium">Live on API Network</span>
                <button
                  type="button"
                  onClick={() => openExternal(listingUrl)}
                  className="ml-auto inline-flex items-center gap-1 text-xs underline"
                >
                  View page <ExternalLink size={12} />
                </button>
              </div>
            )}

            <div>
              <label className="modal-logo-label block mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                className="modal-input w-full"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="modal-logo-label block mb-1">Summary</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value.slice(0, 280))}
                maxLength={280}
                rows={3}
                className="modal-input w-full resize-none"
                disabled={submitting}
              />
              <p className="modal-logo-hint mt-1">{summary.length}/280</p>
            </div>

            <div>
              <label className="modal-logo-label block mb-1">Category</label>
              <div className="modal-picker flex w-full">
                <CompactSelectPicker
                  value={category}
                  onChange={setCategory}
                  options={categoryOptions}
                  aria-label="Category"
                  disabled={submitting}
                />
              </div>
            </div>

            <div>
              <label className="modal-logo-label block mb-1">Tags (optional)</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="payments, billing, webhooks"
                className="modal-input w-full"
                disabled={submitting}
              />
              <p className="modal-logo-hint mt-1">Comma-separated, up to 10 tags</p>
            </div>

            <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900/50 px-3 py-2">
              <p className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Will appear at</p>
              <p className="text-sm font-mono text-gray-800 dark:text-zinc-200 break-all">
                apiboo.com/apis/{slugPreview}
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting || unpublishing}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && <Loader className="animate-spin" size={16} />}
              <span>
                {submitting
                  ? 'Saving…'
                  : isPublished
                    ? 'Update listing'
                    : 'Publish to API Network'}
              </span>
            </button>

            {isPublished && (
              <button
                type="button"
                onClick={handleUnpublish}
                disabled={submitting || unpublishing}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-zinc-600 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-zinc-300 transition-colors hover:bg-gray-50 dark:hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {unpublishing && <Loader className="animate-spin" size={14} />}
                <span>{unpublishing ? 'Removing…' : 'Unpublish from network'}</span>
              </button>
            )}
          </>
        )}
      </form>
    </Modal>
  );
}
